// components/KnowledgeInteractions.js
import { useState, useEffect } from 'react'
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  increment,
  serverTimestamp,
  orderBy
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import toast from 'react-hot-toast'
import { app } from '../firebase'
import UserService from '../services/UserService'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import CommentIcon from '@mui/icons-material/Comment'
import DeleteIcon from '@mui/icons-material/Delete'
import ReplayIcon from '@mui/icons-material/Replay'
import ConfirmModal from './ConfirmModal'
import LoadingSpinner from './LoadingSpinner'
import './KnowledgeInteractions.css'

function KnowledgeInteractions ({
  knowledgeId,
  currentUser,
  userRole,
  onKnowledgeDeleted,
  onKnowledgeReverted
}) {
  const [reactions, setReactions] = useState({ likes: 0, dislikes: 0 })
  const [userReaction, setUserReaction] = useState(null)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [showAllComments, setShowAllComments] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  })
  const [reverting, setReverting] = useState(false)

  const db = getFirestore(app)
  const functions = getFunctions(app)

  // Lade Reactions und Comments beim Mount
  useEffect(() => {
    loadReactions()
    loadComments()
  }, [knowledgeId])

  const loadReactions = async () => {
    try {
      if (!currentUser) return

      const companyId = await UserService.getUserCompanyId(currentUser.uid)
      const reactionsRef = collection(db, 'companies', companyId, 'reactions')
      const q = query(reactionsRef, where('knowledgeId', '==', knowledgeId))
      const snapshot = await getDocs(q)

      let likes = 0
      let dislikes = 0
      let userReactionDoc = null

      snapshot.docs.forEach(doc => {
        const data = doc.data()
        if (data.type === 'like') likes++
        if (data.type === 'dislike') dislikes++

        if (
          currentUser &&
          (data.userId === currentUser.uid || data.userId === currentUser.email)
        ) {
          userReactionDoc = { id: doc.id, type: data.type }
        }
      })

      setReactions({ likes, dislikes })
      setUserReaction(userReactionDoc)
    } catch (error) {
      console.error('Error loading reactions:', error)
    }
  }

  const loadComments = async () => {
    try {
      if (!currentUser) return

      const companyId = await UserService.getUserCompanyId(currentUser.uid)
      const commentsRef = collection(db, 'companies', companyId, 'comments')
      const q = query(
        commentsRef,
        where('knowledgeId', '==', knowledgeId),
        orderBy('timestamp', 'desc')
      )
      const snapshot = await getDocs(q)

      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      setComments(commentsData)
    } catch (error) {
      console.error('Error loading comments:', error)
    }
  }

  const handleReaction = async type => {
    if (!currentUser) {
      toast.error('Bitte melden Sie sich an, um zu bewerten')
      return
    }

    setLoading(true)
    try {
      const companyId = await UserService.getUserCompanyId(currentUser.uid)
      const knowledgeRef = doc(
        db,
        'companies',
        companyId,
        'knowledge',
        knowledgeId
      )

      // User hat bereits diese Reaktion - entfernen
      if (userReaction && userReaction.type === type) {
        await deleteDoc(
          doc(db, 'companies', companyId, 'reactions', userReaction.id)
        )
        await updateDoc(knowledgeRef, {
          [`stats.${type}s`]: increment(-1),
          'stats.helpfulScore': increment(type === 'like' ? -1 : 1)
        })
        setUserReaction(null)
        setReactions(prev => ({
          ...prev,
          [type + 's']: prev[type + 's'] - 1
        }))
      }
      // User wechselt von like zu dislike oder umgekehrt
      else if (userReaction && userReaction.type !== type) {
        await deleteDoc(
          doc(db, 'companies', companyId, 'reactions', userReaction.id)
        )
        const newReactionRef = await addDoc(
          collection(db, 'companies', companyId, 'reactions'),
          {
            knowledgeId,
            userId: currentUser.uid,
            userName: currentUser.displayName || currentUser.email,
            type,
            timestamp: serverTimestamp()
          }
        )

        const oldType = userReaction.type
        await updateDoc(knowledgeRef, {
          [`stats.${oldType}s`]: increment(-1),
          [`stats.${type}s`]: increment(1),
          'stats.helpfulScore': increment(type === 'like' ? 2 : -2)
        })

        setUserReaction({ id: newReactionRef.id, type })
        setReactions(prev => ({
          likes: prev.likes + (type === 'like' ? 1 : -1),
          dislikes: prev.dislikes + (type === 'dislike' ? 1 : -1)
        }))
      }
      // Neue Reaktion
      else {
        const newReactionRef = await addDoc(
          collection(db, 'companies', companyId, 'reactions'),
          {
            knowledgeId,
            userId: currentUser.uid,
            userName: currentUser.displayName || currentUser.email,
            type,
            timestamp: serverTimestamp()
          }
        )

        await updateDoc(knowledgeRef, {
          [`stats.${type}s`]: increment(1),
          'stats.helpfulScore': increment(type === 'like' ? 1 : -1)
        })

        setUserReaction({ id: newReactionRef.id, type })
        setReactions(prev => ({
          ...prev,
          [type + 's']: prev[type + 's'] + 1
        }))
      }
    } catch (error) {
      console.error('Error handling reaction:', error)
      toast.error('Fehler beim Speichern der Bewertung')
    } finally {
      setLoading(false)
    }
  }

  const handleAddComment = async e => {
    e.preventDefault()

    if (!currentUser) {
      toast.error('Bitte melden Sie sich an, um zu kommentieren')
      return
    }

    if (!newComment.trim()) return

    setLoading(true)
    try {
      const companyId = await UserService.getUserCompanyId(currentUser.uid)

      await addDoc(collection(db, 'companies', companyId, 'comments'), {
        knowledgeId,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        text: newComment.trim(),
        timestamp: serverTimestamp(),
        edited: false
      })

      await updateDoc(
        doc(db, 'companies', companyId, 'knowledge', knowledgeId),
        {
          'stats.commentCount': increment(1)
        }
      )

      setNewComment('')
      await loadComments()
    } catch (error) {
      console.error('Error adding comment:', error)
      toast.error('Fehler beim Hinzufügen des Kommentars')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteComment = commentId => {
    setConfirmModal({
      isOpen: true,
      title: 'Kommentar löschen',
      message: 'Möchten Sie diesen Kommentar wirklich löschen?',
      onConfirm: () => deleteComment(commentId),
      type: 'danger'
    })
  }

  const deleteComment = async commentId => {
    setLoading(true)
    try {
      const companyId = await UserService.getUserCompanyId(currentUser.uid)

      await deleteDoc(doc(db, 'companies', companyId, 'comments', commentId))
      await updateDoc(
        doc(db, 'companies', companyId, 'knowledge', knowledgeId),
        {
          'stats.commentCount': increment(-1)
        }
      )
      await loadComments()
    } catch (error) {
      console.error('Error deleting comment:', error)
      toast.error('Fehler beim Löschen des Kommentars')
    } finally {
      setLoading(false)
    }
  }

  // ⭐ NEU: Knowledge komplett löschen (Admin only)
  const handleDeleteKnowledge = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Wissen löschen',
      message:
        '⚠️ ACHTUNG: Dieses Wissen wird permanent aus Firestore UND Pinecone gelöscht!\n\nAlle Kommentare und Reaktionen gehen verloren.\n\nWirklich fortfahren?',
      onConfirm: () => deleteKnowledge(),
      type: 'danger'
    })
  }

  const deleteKnowledge = async () => {
    setDeleting(true)
    try {
      const deleteKnowledgeFn = httpsCallable(functions, 'deleteKnowledge')
      const result = await deleteKnowledgeFn({ knowledgeId })

      toast.success(result.data.message)

      // Optional: Parent-Component benachrichtigen (z.B. für Navigation zurück)
      if (onKnowledgeDeleted) {
        onKnowledgeDeleted(knowledgeId)
      }
    } catch (error) {
      console.error('Error deleting knowledge:', error)
      toast.error('Fehler beim Löschen: ' + error.message)
    } finally {
      setDeleting(false)
    }
  }

  const handleRevertKnowledge = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Re-Edit anfordern',
      message:
        'Das Wissen wird zurück auf „wartet auf Freigabe“ gesetzt und der Pinecone-Eintrag wird entfernt. Anschließend muss es erneut moderiert werden.\n\nFortfahren?',
      onConfirm: () => revertKnowledge(),
      type: 'warning'
    })
  }

  const revertKnowledge = async () => {
    setReverting(true)
    try {
      const revertFn = httpsCallable(functions, 'revertKnowledgeToPending')
      const result = await revertFn({ knowledgeId })
      toast.success(result.data?.message || 'Wissen wurde zurückgesetzt')

      if (onKnowledgeReverted) {
        onKnowledgeReverted(knowledgeId)
      }
    } catch (error) {
      console.error('Error reverting knowledge:', error)
      toast.error('Fehler beim Zurücksetzen: ' + error.message)
    } finally {
      setReverting(false)
    }
  }

  const formatTimestamp = timestamp => {
    if (!timestamp) return 'Gerade eben'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const helpfulScore = reactions.likes - reactions.dislikes
  const isAdminOrModerator =
    userRole === 'admin' ||
    userRole === 'superadmin' ||
    userRole === 'moderator'

  // Zeige neuesten Kommentar als Preview
  const latestComment = comments.length > 0 ? comments[0] : null
  const remainingComments = comments.length > 1 ? comments.slice(1) : []

  return (
    <div className='knowledge-interactions'>
      {/* Reactions Section */}
      <div className='reactions-section'>
        <div className='reactions-display'>
          <span className='helpful-score' data-positive={helpfulScore > 0}>
            {helpfulScore > 0 && '+'}
            {helpfulScore} Hilfreich
          </span>
          <span className='reaction-count'>
            <ThumbUpIcon fontSize='small' />
            {reactions.likes}
          </span>
          <span className='reaction-count'>
            <ThumbDownIcon fontSize='small' />
            {reactions.dislikes}
          </span>
        </div>

        <div className='reactions-buttons'>
          <button
            className={`reaction-btn ${
              userReaction?.type === 'like' ? 'active' : ''
            }`}
            onClick={() => handleReaction('like')}
            disabled={loading}
            title='Hilfreich'
          >
            <ThumbUpIcon fontSize='small' />
            Hilfreich
          </button>

          <button
            className={`reaction-btn ${
              userReaction?.type === 'dislike' ? 'active' : ''
            }`}
            onClick={() => handleReaction('dislike')}
            disabled={loading}
            title='Nicht hilfreich'
          >
            <ThumbDownIcon fontSize='small' />
            Nicht hilfreich
          </button>
        </div>
      </div>

      {/* Comments Section */}
      <div className='comments-section'>
        <div className='comments-header'>
          <h4>
            <CommentIcon fontSize='small' />
            Kommentare
            {comments.length > 0 && (
              <span className='comments-badge'>{comments.length}</span>
            )}
          </h4>
        </div>

        {/* Comment Form */}
        <form onSubmit={handleAddComment} className='comment-form'>
          <textarea
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder='Kommentar hinzufügen...'
            rows={3}
            disabled={loading}
          />
          <button type='submit' disabled={loading || !newComment.trim()}>
            Kommentieren
          </button>
        </form>

        {/* Latest Comment Preview */}
        {latestComment && (
          <div className='comment-item latest'>
            <div className='comment-header'>
              <span className='comment-author'>{latestComment.userName}</span>
              <span className='comment-date'>
                {formatTimestamp(latestComment.timestamp)}
              </span>
            </div>
            <p className='comment-text'>{latestComment.text}</p>
            {currentUser &&
              (latestComment.userId === currentUser.uid ||
                latestComment.userId === currentUser.email) && (
                <button
                  className='delete-comment-btn'
                  onClick={() => handleDeleteComment(latestComment.id)}
                  disabled={loading}
                >
                  <DeleteIcon fontSize='small' />
                  Löschen
                </button>
              )}
          </div>
        )}

        {/* Show More Button */}
        {remainingComments.length > 0 && (
          <button
            className='show-more-comments-btn'
            onClick={() => setShowAllComments(!showAllComments)}
          >
            {showAllComments
              ? 'Weniger anzeigen'
              : `${remainingComments.length} weitere ${
                  remainingComments.length === 1 ? 'Kommentar' : 'Kommentare'
                } anzeigen`}
          </button>
        )}

        {/* Additional Comments */}
        {showAllComments && (
          <div className='comments-list'>
            {remainingComments.map(comment => (
              <div key={comment.id} className='comment-item'>
                <div className='comment-header'>
                  <span className='comment-author'>{comment.userName}</span>
                  <span className='comment-date'>
                    {formatTimestamp(comment.timestamp)}
                  </span>
                </div>
                <p className='comment-text'>{comment.text}</p>
                {currentUser &&
                  (comment.userId === currentUser.uid ||
                    comment.userId === currentUser.email) && (
                    <button
                      className='delete-comment-btn'
                      onClick={() => handleDeleteComment(comment.id)}
                      disabled={loading}
                    >
                      <DeleteIcon fontSize='small' />
                      Löschen
                    </button>
                  )}
              </div>
            ))}
          </div>
        )}
        {/* ⭐ NEU: Admin Delete Button */}
        {isAdminOrModerator && (
          <div className='admin-actions'>
            <button
              className='revert-knowledge-btn'
              onClick={handleRevertKnowledge}
              disabled={reverting || deleting}
            >
              <ReplayIcon fontSize='small' />
              {reverting ? 'Wird zurückgesetzt...' : 'Re-Edit'}
            </button>
            <button
              className='delete-knowledge-btn'
              onClick={handleDeleteKnowledge}
              disabled={deleting}
            >
              <DeleteIcon fontSize='small' />
              {deleting ? 'Wird gelöscht...' : 'Wissen löschen'}
            </button>
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal({ ...confirmModal, isOpen: false, onConfirm: null })
        }
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
      />
      {/* Loading Spinner - wird angezeigt wenn irgendetwas lädt */}
      {(loading || deleting || reverting) && <LoadingSpinner />}
    </div>
  )
}

export default KnowledgeInteractions
